import React, { useState } from 'react';
import api from '../api/api';
import {
  FaTimes,
  FaKey,
  FaEye,
  FaEyeSlash,
  FaExclamationTriangle,
  FaCheckCircle,
  FaShieldAlt,
} from 'react-icons/fa';

// Strong password regex: 8+ chars, uppercase, lowercase, number, special char
const STRONG_PWD = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>_+\-=]).{8,}$/;

function validateStrongPassword(pwd) {
  if (!pwd) return 'Password is required.';
  if (pwd.length < 8) return 'Password must be at least 8 characters long.';
  if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one uppercase letter (A-Z).';
  if (!/[a-z]/.test(pwd)) return 'Password must contain at least one lowercase letter (a-z).';
  if (!/[0-9]/.test(pwd)) return 'Password must contain at least one number (0-9).';
  if (!/[!@#$%^&*(),.?":{}|<>_+\-=]/.test(pwd))
    return 'Password must contain at least one special character (!@#$%...).';
  return null;
}

function StrengthBar({ password }) {
  const checks = [
    { label: '8+ chars', ok: password.length >= 8 },
    { label: 'A-Z', ok: /[A-Z]/.test(password) },
    { label: 'a-z', ok: /[a-z]/.test(password) },
    { label: '0-9', ok: /[0-9]/.test(password) },
    { label: '!@#', ok: /[!@#$%^&*(),.?":{}|<>_+\-=]/.test(password) },
  ];
  const passed = checks.filter((c) => c.ok).length;
  const colors = ['#ef4444', '#f59e0b', '#f59e0b', '#10b981', '#6366f1'];
  const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];

  if (!password) return null;

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
        {checks.map((_, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: '4px',
              borderRadius: '2px',
              background: i < passed ? colors[passed - 1] : 'rgba(255,255,255,0.1)',
              transition: 'background 0.3s ease',
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {checks.map((c) => (
            <span
              key={c.label}
              style={{
                fontSize: '0.68rem',
                color: c.ok ? '#34d399' : 'var(--text-dim)',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
              }}
            >
              {c.ok ? '✓' : '○'} {c.label}
            </span>
          ))}
        </div>
        <span style={{ fontSize: '0.68rem', color: colors[passed - 1] || 'var(--text-dim)', fontWeight: 600 }}>
          {passed > 0 ? labels[passed - 1] : ''}
        </span>
      </div>
    </div>
  );
}

export default function ResetPasswordModal({ isOpen, user, onClose }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen || !user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const pwdError = validateStrongPassword(newPassword);
    if (pwdError) {
      setError(pwdError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match. Please confirm correctly.');
      return;
    }

    setSaving(true);
    try {
      const res = await api.post(`/users/${user.userid}/reset-password`, {
        newPassword,
      });

      if (res.data.success) {
        setSuccess(`Password for ${user.username} successfully updated!`);
        setTimeout(() => {
          setSuccess('');
          setNewPassword('');
          setConfirmPassword('');
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px' }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-md)',
                background: 'rgba(245, 158, 11, 0.2)',
                color: 'var(--warning)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FaKey />
            </div>
            <div>
              <h3 style={{ fontSize: '1.15rem' }}>Reset User Password</h3>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                Target Account: <strong>{user.username}</strong> ({user.name})
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.2rem',
            }}
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div
                style={{
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-md)',
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#fca5a5',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                }}
              >
                <FaExclamationTriangle />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div
                style={{
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-md)',
                  background: 'rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  color: '#6ee7b7',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                }}
              >
                <FaCheckCircle />
                <span>{success}</span>
              </div>
            )}

            {/* Policy reminder */}
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                background: 'rgba(99, 102, 241, 0.08)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
                fontSize: '0.78rem',
                color: 'var(--text-muted)',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
              }}
            >
              <FaShieldAlt style={{ color: 'var(--primary)', marginTop: '2px', flexShrink: 0 }} />
              <span>
                Password Policy: Minimum <strong>8 characters</strong>, must include{' '}
                <strong>uppercase</strong> (A-Z), <strong>lowercase</strong> (a-z),{' '}
                <strong>number</strong> (0-9) and <strong>special character</strong> (!@#$%...).
              </span>
            </div>

            <div className="form-group">
              <label className="form-label">New Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNew ? 'text' : 'password'}
                  className="form-control"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 8 chars: A-Z, a-z, 0-9, !@#$..."
                  required
                  style={{ paddingRight: '40px' }}
                />
                <span
                  onClick={() => setShowNew(!showNew)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-dim)',
                    cursor: 'pointer',
                  }}
                >
                  {showNew ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
              <StrengthBar password={newPassword} />
            </div>

            <div className="form-group">
              <label className="form-label">Confirm New Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  className="form-control"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  style={{ paddingRight: '40px' }}
                />
                <span
                  onClick={() => setShowConfirm(!showConfirm)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-dim)',
                    cursor: 'pointer',
                  }}
                >
                  {showConfirm ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
              {confirmPassword && newPassword === confirmPassword && (
                <span style={{ fontSize: '0.75rem', color: '#34d399', marginTop: '4px', display: 'block' }}>
                  ✓ Passwords match
                </span>
              )}
              {confirmPassword && newPassword !== confirmPassword && (
                <span style={{ fontSize: '0.75rem', color: '#fca5a5', marginTop: '4px', display: 'block' }}>
                  ✗ Passwords do not match
                </span>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
